// react
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
// @mui
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
// utils
import { AxiosPost } from '../../utils/axios';
// hooks
import useAuth from '../../hooks/useAuth';
// components
import Iconify from '../Iconify';

const ERROR_COPY = {
  invalid: 'Código inválido o no encontrado.',
  inactive: 'Este código está inactivo.',
  expired: 'Este código ya expiró.',
  not_for_this_user: 'Este código no es para tu cuenta.',
  exhausted: 'Este código ya alcanzó su límite de usos.',
  already_redeemed: 'Ya canjeaste este código.',
  missing_price: 'El código no está completamente configurado. Avísale al administrador.',
  wrong_branch: 'Este código no se puede aplicar aquí.',
  invalid_provider: 'Método de pago no soportado.',
  provider_error: 'No pudimos iniciar el pago. Intenta de nuevo en unos minutos.',
};

RedeemCodeInput.propTypes = {
  paymentProvider: PropTypes.oneOf(['stripe', 'paypal']),
  onCredited: PropTypes.func,
  title: PropTypes.string,
  placeholder: PropTypes.string,
};

/**
 * Input para canjear un GiftCode emitido por admin.
 *
 * Comportamiento:
 *  - Si el código es 100% gratis y sin force_payment_flow → acredita
 *    inmediatamente y llama `onCredited()` (el padre refresca user state).
 *  - Si requiere checkout → redirect a la URL del proveedor.
 */
export default function RedeemCodeInput({
  paymentProvider = 'stripe',
  onCredited,
  title = '¿Tienes un código de regalo?',
  placeholder = 'Ingresa tu código',
}) {
  const { refreshUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setError(null);
    setBusy(true);
    try {
      const res = await AxiosPost('/payments/redeem_code/', {
        code: trimmed,
        payment_provider: paymentProvider,
      });
      const body = res?.data?.response;
      if (body?.status === 'credited') {
        enqueueSnackbar(`Listo. Se acreditó: ${body.product}`, { variant: 'success' });
        // Hydrate user so available_credits / perks reflect the new state
        if (refreshUser) await refreshUser();
        if (onCredited) onCredited(body);
        setCode('');
      } else if (body?.status === 'checkout' && body.url) {
        window.location.href = body.url;
      } else {
        setError(ERROR_COPY.provider_error);
      }
    } catch (e) {
      // Axios interceptor unwraps error.response.data → e is already the body.
      const reason = e?.error;
      setError(ERROR_COPY[reason] || ERROR_COPY.provider_error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={code}
          disabled={busy}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
          error={Boolean(error)}
        />
        <Button
          variant="outlined"
          color="primary"
          onClick={handleSubmit}
          disabled={busy || !code.trim()}
          startIcon={
            busy ? (
              <CircularProgress size={16} />
            ) : (
              <Iconify icon="mdi:gift-outline" />
            )
          }
        >
          Canjear
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
